<template>
  <div>
    <h3 class="mb-2">Мультисоздание</h3>

    <b-card title="Параметры РК">
      <multi-campaign-form ref="campaignForm" :validation-schema="campaignValidation" />
    </b-card>

    <b-card>
      <b-form-group label="VAST URL's" label-for="vast-urls">
        <b-form-textarea
            id="vast-urls"
            v-model="vastUrls"
            rows="8"
            placeholder="По одному VAST URL в строке"
            :disabled="isRunning"
        />
      </b-form-group>
    </b-card>

    <b-card title="Параметры размещения">
      <multi-placement-form ref="placementForm" :validation-schema="placementValidation" />
    </b-card>

    <div class="mb-2">
      <loader-button
          variant="primary"
          :disabled="isRunning"
          :is-submitting="isRunning"
          @click.native="startMassCreation"
      >
        Создать
      </loader-button>
    </div>

    <b-card v-if="log" title="Лог">
      <b-form-textarea :value="log" rows="10" readonly class="multi-creation__mono" />
    </b-card>

    <b-card v-if="createdVasts.length" title="Созданные VAST">
      <b-form-textarea :value="createdVasts.join('\n')" rows="8" readonly class="multi-creation__mono" />
    </b-card>
  </div>
</template>

<script>
import { BCard, BFormGroup, BFormTextarea } from 'bootstrap-vue'
import LoaderButton from '@/components/LoaderButton'
import MultiCampaignForm from '@/components/MultiCampaignForm'
import MultiPlacementForm from '@/components/MultiPlacementForm'
import { CREATE_CAMPAIGN_VALIDATION, CREATE_PLACEMENT_VALIDATION } from '@/constants/validation'
import { createCampaign } from '@/api/campigns'
import { createPlacement } from '@/api/placements'
import { generateMulticreationName } from '@/scripts/generateMulticreationName'
import { getToastificationErrorParams, getToastificationSuccessParams } from '@/helpers/toasts'

const VAST_DOMAIN = 's24tv.net'
const extractId = (response) => response?.data?.data?.id ?? response?.data?.id ?? null

export default {
  name: 'MultiCreation',
  components: {
    BCard,
    BFormGroup,
    BFormTextarea,
    LoaderButton,
    MultiCampaignForm,
    MultiPlacementForm
  },
  data() {
    return {
      campaignValidation: { ...CREATE_CAMPAIGN_VALIDATION, tag: undefined },
      placementValidation: { ...CREATE_PLACEMENT_VALIDATION, campaigns: undefined, name: undefined },
      vastUrls: '',
      log: '',
      createdVasts: [],
      isRunning: false
    }
  },
  methods: {
    appendLog(message) {
      this.log += (this.log ? '\n' : '') + message
    },

    parseVastUrls() {
      const result = []
      this.vastUrls.split('\n').forEach((line) => {
        const url = line.trim()
        if (!url) return
        try {
          if (new URL(url)) result.push(url)
        } catch (e) {
          this.appendLog(`Пропущен невалидный URL: ${url}`)
        }
      })
      return result
    },

    async startMassCreation() {
      if (this.isRunning) return

      this.log = ''
      this.createdVasts = []

      const isCampaignValid = await this.$refs.campaignForm.validate()
      const isPlacementValid = await this.$refs.placementForm.validate()

      if (!isCampaignValid || !isPlacementValid) {
        this.$toast(getToastificationErrorParams('Заполните обязательные поля форм'))
        return
      }

      const urls = this.parseVastUrls()

      if (!urls.length) {
        this.appendLog('Нет валидных VAST URL для обработки.')
        return
      }

      this.isRunning = true

      const campaignBase = this.$refs.campaignForm.getRequestData()
      const placementBase = this.$refs.placementForm.getRequestData()
      const baseName = campaignBase.name

      this.appendLog(`Найдено валидных URL: ${urls.length}. Начинаю создание...`)

      for (let i = 0; i < urls.length; i++) {
        const vastURL = urls[i]
        const hash = await generateMulticreationName(vastURL)
        const name = `${baseName} ${hash} ${i + 1}`

        try {
          this.appendLog(`[${i + 1}/${urls.length}] Создаю РК "${name}"...`)
          const campaignData = { ...campaignBase, name, tag: vastURL }
          const campaignRes = await createCampaign(campaignData)
          const campaignId = extractId(campaignRes)

          if (!campaignId) {
            this.appendLog(`[${i + 1}/${urls.length}] РК создана, но не удалось получить её id — размещение пропущено.`)
            continue
          }

          this.appendLog(`[${i + 1}/${urls.length}] РК создана (id ${campaignId}). Создаю размещение...`)
          const placementData = { ...placementBase, name, campaigns: String(campaignId) }
          const placementRes = await createPlacement(placementData)
          const placementId = extractId(placementRes)

          if (!placementId) {
            this.appendLog(`[${i + 1}/${urls.length}] Размещение создано, но не удалось получить его id — VAST-ссылку не сформировать.`)
            continue
          }

          const vastLink = `https://${VAST_DOMAIN}/v1/tag/?id=${placementId}&vast=1`
          this.createdVasts.push(vastLink)
          this.appendLog(`[${i + 1}/${urls.length}] Готово: размещение id ${placementId}.`)
        } catch (e) {
          const errMessage = e?.response?.data?.message || e?.message || 'неизвестная ошибка'
          this.appendLog(`[${i + 1}/${urls.length}] Ошибка для ${vastURL}: ${errMessage}`)
        }
      }

      this.appendLog(`Завершено. Успешно создано: ${this.createdVasts.length} из ${urls.length}.`)
      this.$toast(getToastificationSuccessParams(`Создано ${this.createdVasts.length} из ${urls.length}`))
      this.isRunning = false
    }
  }
}
</script>

<style lang="scss" scoped>
.multi-creation__mono {
  font-family: monospace;
  font-size: 13px;
}
</style>
